import { emptyToUndefined, MPOST, POST } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { OPERATION, Permission } from 'types/Permission.ts';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Input } from 'ui/control/Input.tsx';
import { Select } from 'ui/control/Select.tsx';
import { SelectMulti } from 'ui/control/SelectMulti.tsx';
import { useConfirm } from '#admin/component/DialogProvider.tsx';
import { SwitchResourceModeButton } from '#admin/component/SwitchResourceModeButton.tsx';

type AddPermissionFormProps = {
  user?: User;
  onAdd: (values: Permission) => void;
};

type FormValues = {
  subject: string;
  operation: OPERATION | '';
  resource: string;
};

export const AddPermissionForm = ({ user, onAdd }: AddPermissionFormProps) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [resourceMode, setResourceMode] = React.useState<'select' | 'text'>('select');

  const allUsersSWR = useSWR<User[]>(['/user/list', { sort: [['name', 'asc']] }], POST);
  const allOperationsSWR = useSWR<string[]>(['/permission/list-operations', {}], POST);

  const userIdUserMap = React.useMemo(() => {
    if (!allUsersSWR.data) return {};
    return allUsersSWR.data.reduce(
      (acc, user) => {
        acc[user.id] = user;
        return acc;
      },
      {} as Record<string, User>,
    );
  }, [allUsersSWR.data]);

  const createPermissionSWR = useSWRMutation('/permission', MPOST);

  const availableSubjects = React.useMemo(() => {
    if (!allUsersSWR.data) return {};
    const mappedUsers = allUsersSWR.data.reduce(
      (acc, user) => {
        acc[user.id] = user.name + (user.is_group ? ' (Group)' : '');
        return acc;
      },
      {} as Record<string, string>,
    );
    return mappedUsers;
  }, [allUsersSWR.data]);

  const availableResources = React.useMemo(() => {
    if (!allUsersSWR.data) return {};
    const mappedUsers = allUsersSWR.data.reduce(
      (acc, user) => {
        acc[user.id] = user.name + (user.is_group ? ' (Group)' : '');
        return acc;
      },
      {} as Record<string, string>,
    );
    return mappedUsers;
  }, [allUsersSWR.data]);

  const availableOperations = React.useMemo(() => {
    if (!allOperationsSWR.data) return {};
    const mappedOperations = allOperationsSWR.data
      .filter((operation) => operation !== '*')
      .reduce(
        (acc, operation) => {
          acc[operation] = operation;
          return acc;
        },
        {} as Record<string, string>,
      );
    return {
      '*': t('forms.permission.operation.allOperations'),
      ...mappedOperations,
    };
  }, [allOperationsSWR.data, t]);

  const formRef = React.useRef<HTMLFormElement>(null);
  const formContext = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      subject: user?.id || '', // tofix: forms can only hanbdle strings, remove undefined from here
      operation: '',
      resource: '',
    },
  });
  const { handleSubmit, reset } = formContext;

  const onSubmit = async (values: FormValues) => {
    if (!(await confirm(t('forms.permission.confirmAdd')))) return;
    const subject_is_group = userIdUserMap[values.subject]?.is_group ?? false; // Intentional '?' here
    const resource_is_owner = resourceMode === 'select';
    const permission = await createPermissionSWR.trigger({ ...emptyToUndefined(values), subject_is_group, resource_is_owner });
    void onAdd(permission);
    reset();
  };

  const isLoading = allUsersSWR.isLoading || allOperationsSWR.isLoading;

  return (
    <FormProvider {...formContext}>
      <form
        id="add-permission-form"
        ref={formRef}
        className="relative flex min-h-[100px] flex-row items-end gap-4 bg-blue-sky p-6"
        noValidate={true}
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        {isLoading && <Spinner centered={true} />}
        {!isLoading && (
          <React.Fragment>
            <Select
              className="w-[240px]"
              name={'subject'}
              label={t('forms.permission.subject.label')}
              placeholder={t('forms.permission.subject.placeholder')}
              required={true}
              autoComplete={'off'}
              options={availableSubjects}
              registerOptions={{ required: true }}
              readOnly={!!user}
            />
            <SelectMulti
              className="w-[240px]"
              name={'operation'}
              label={t('forms.permission.operation.label')}
              options={availableOperations}
              placeholder={t('forms.permission.operation.placeholder')}
              selectable={'one'}
              closeOnChangedValue={true}
              hasClearAll={true}
              required={true}
            />
            {resourceMode === 'select' ? (
              <SelectMulti
                className="w-[240px]"
                name={'resource'}
                label={
                  <SwitchResourceModeButton
                    text={t('forms.permission.resource.label')}
                    resourceMode={resourceMode}
                    onClick={() => setResourceMode((prevMode) => (prevMode === 'select' ? 'text' : 'select'))}
                  />
                }
                options={availableResources}
                placeholder={t('forms.permission.resource.placeholder.select')}
                selectable={'one'}
                closeOnChangedValue={true}
                hasClearAll={true}
                required={true}
              />
            ) : (
              <Input
                className="w-[240px]"
                name={'resource'}
                label={
                  <SwitchResourceModeButton
                    text={t('forms.permission.resource.label')}
                    resourceMode={resourceMode}
                    onClick={() => setResourceMode((prevMode) => (prevMode === 'select' ? 'text' : 'select'))}
                  />
                }
                placeholder={t('forms.permission.resource.placeholder.text')}
                required={true}
                autoComplete={'off'}
                registerOptions={{ required: true }}
              />
            )}
            <Button loading={createPermissionSWR.isMutating} className="blue h-[37px] w-[150px] max-w-[150px]" type="submit">
              {t('forms.permission.buttons.add')}
            </Button>
          </React.Fragment>
        )}
      </form>
    </FormProvider>
  );
};
